import { ActivityIndicator, ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { Text, View } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { isWeb } from "@src/utils/web-helpers";
import {
  ReportNotFoundError,
  ReportNotReadyError,
  useWasteSenseReport,
} from "@src/features/wastesense/hooks/useWasteSenseReport";
import { WasteSenseHero } from "@src/features/wastesense/components/WasteSenseHero";
import { DumpDetections } from "@src/features/wastesense/components/DumpDetections";
import { WeeklySummary } from "@src/features/wastesense/components/WeeklySummary";
import { SitesPanel } from "@src/features/wastesense/components/SitesPanel";
import type { WasteSensePayload } from "@visioni/a3s-reports/client-messages";
import type { ISODate } from "@visioni/a3s-codecs";

/**
 * `?periodStart=YYYY-MM-DD` is the natural key for a report (paired
 * with the type segment in the URL — `wastesense`). The list view
 * builds these links with `encodeURIComponent`, but the constraint
 * is enforced here too in case a stray, malformed value lands in the
 * URL.
 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export default function WasteSenseReportScreen() {
  const params = useLocalSearchParams<{ periodStart?: string }>();
  const periodStart =
    typeof params.periodStart === "string" && ISO_DATE_RE.test(params.periodStart) ?
      (params.periodStart as ISODate)
    : undefined;

  const { report, payload, isLoading, isError, error, isTenantPending } = useWasteSenseReport(periodStart);

  if (!isWeb) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" padding="3xl" gap="md">
        <Text textStyle="sm/semibold" color="text" textAlign="center">
          WasteSense reports are only available on the web client.
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Open this report in your browser to view it.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{
        paddingTop: 40,
        paddingBottom: 40,
        paddingHorizontal: 40,
        maxWidth: sizes.webReportMaxWidth,
        width: "100%",
      }}
      showsVerticalScrollIndicator={false}
    >
      <Body
        periodStart={periodStart}
        payload={payload}
        generatedAt={report?.generatedAt}
        isLoading={isLoading}
        isError={isError}
        error={error}
        isTenantPending={isTenantPending}
      />
    </ScrollView>
  );
}

interface BodyProps {
  periodStart: ISODate | undefined;
  payload: WasteSensePayload | undefined;
  /** ISO instant the report was last generated — passed to the hero's "Generated At" line. */
  generatedAt: string | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isTenantPending: boolean;
}

function Body({ periodStart, payload, generatedAt, isLoading, isError, error, isTenantPending }: BodyProps) {
  if (!periodStart) {
    return (
      <Message
        title="Missing report period"
        body="This URL is missing a valid `periodStart` (YYYY-MM-DD). Pick a report from the list to continue."
      />
    );
  }

  if (isTenantPending) {
    return <Loading label="Resolving tenant…" />;
  }

  if (isLoading && !payload) {
    return <Loading label="Loading report…" />;
  }

  if (isError && error instanceof ReportNotFoundError) {
    return (
      <Message
        title="Report not found"
        body={`No WasteSense report exists for the period starting ${periodStart}. It may have been deleted or never generated.`}
      />
    );
  }

  if (isError && error instanceof ReportNotReadyError) {
    const detail =
      error.status === "failed" ?
        `The report for ${periodStart} failed to generate. Check the report job logs or regenerate it.`
      : `The report for ${periodStart} is still being generated. Refresh in a minute.`;
    return <Message title="Report not ready" body={detail} />;
  }

  if (isError) {
    return <Message title="Couldn't load report" body={error?.message ?? "An unexpected error occurred."} />;
  }

  if (!payload) {
    // Decode failed (logged by the hook) — surface a generic message
    // so the screen never falls through to a blank container.
    return (
      <Message
        title="Report unavailable"
        body="The report payload could not be decoded. The schema may be out of date — please reload the page."
      />
    );
  }

  return <ReportContent payload={payload} generatedAt={generatedAt} />;
}

interface MessageProps {
  title: string;
  body: string;
}

function Message({ title, body }: MessageProps) {
  return (
    <View padding="3xl" alignItems="center" gap="sm">
      <Text textStyle="sm/semibold" color="text" textAlign="center">
        {title}
      </Text>
      <Text textStyle="xs/regular" color="textMuted" textAlign="center">
        {body}
      </Text>
    </View>
  );
}

interface LoadingProps {
  label: string;
}

function Loading({ label }: LoadingProps) {
  return (
    <View padding="3xl" alignItems="center" gap="md">
      <ActivityIndicator />
      <Text textStyle="sm/regular" color="textMuted">
        {label}
      </Text>
    </View>
  );
}

interface ReportContentProps {
  payload: WasteSensePayload;
  generatedAt: string | undefined;
}

function ReportContent({ payload, generatedAt }: ReportContentProps) {
  return (
    <View gap="6xl">
      <WasteSenseHero meta={payload.report_meta} generatedAt={generatedAt} />
      {/* Dump Detections leads the report — the most important information first. */}
      <DumpDetections dumps={payload.dump_detections} />
      <WeeklySummary kpis={payload.kpis} />
      <SitesPanel mapSites={payload.map_sites} />
    </View>
  );
}
